import { createContext, useCallback, useContext, useRef } from 'react';
import { ClientConfigurationContext } from '../../context/ClientConfigurationContextProvider';
import { GeaoContext } from './GeaoContextProvider';
import { GeaoPaths } from './constants/GeaoPaths';
import { buildGtmEventMetadata, getPreviousPageName, GtmEventType, trackGtmEvent } from '../../util/gtmTracking';
import { useGtmPageViewId } from '../../context/GtmPageViewContext';

export const OnboardingInteractionContext = createContext(null);

const pageNames = Object.fromEntries(
    Object.entries(GeaoPaths)
        .filter(([key]) => key !== 'PreOnboardingNationalAccountPrefix')
        .map(([key, path]) => [path, `AgencyIdentity:Onboarding:${key}`])
);
Object.assign(pageNames, {
    [GeaoPaths.CreateYourProfileChecklist]: 'AgencyIdentity:OnboardingOverview',
    [GeaoPaths.CreateYourProfile]: 'AgencyIdentity:Pre-onboarding',
    [GeaoPaths.CreateYourProfileKickout]: 'AgencyIdentity:OnboardingKickout',
    [GeaoPaths.PrincipalWelcomeChecklist]: 'AgencyIdentity:AgencyOverview',
    [GeaoPaths.PrincipalPersonalInformation]: 'AgencyIdentity:Onboarding:PrincipalPersonalInfo',
    [GeaoPaths.PrincipalApplicationSubmitted]: 'AgencyIdentity:Onboarding:PrincipalCongrats',
    [GeaoPaths.PrincipalKickout]: 'AgencyIdentity:Onboarding:AgencyKickout',
    [GeaoPaths.ProducerWelcomeChecklist]: 'AgencyIdentity:AgentOverview',
    [GeaoPaths.ProducerPersonalInformation]: 'AgencyIdentity:Onboarding:AgentPersonalInformation',
    [GeaoPaths.ProducerApplicationSubmitted]: 'AgencyIdentity:Onboarding:AgentCongrats',
    [GeaoPaths.ProducerKickout]: 'AgencyIdentity:Onboarding:AgentKickout',
    [GeaoPaths.ApplicationStatus]: 'AgencyIdentity:Onboarding:RecallSearch',
    '/onboarding-interview': 'AgencyIdentity:Onboarding:Interview',
    '/onboarding-commission-tax-form': 'AgencyIdentity:Onboarding:CommissionTaxForm'
});

export function getOnboardingPageName(path) {
    if (!path) {
        return undefined;
    }
    return path.startsWith(GeaoPaths.PreOnboardingNationalAccountPrefix)
        ? 'AgencyIdentity:Pre-onboarding'
        : pageNames[path];
}

const idFields = {
    [GtmEventType.ButtonClick]: 'buttonId',
    [GtmEventType.LinkClick]: 'linkId',
    [GtmEventType.RadioChange]: 'radioId',
    [GtmEventType.CheckboxChange]: 'checkboxId'
};

const widgetActions = {
    'gds-modal': 'Close',
    'gds-toast': 'Dismiss',
    'gds-alert': 'Dismiss',
    'gds-disclaimer': 'Toggle',
    'gds-tooltip': 'ToggleTooltip'
};

function eventElements(event) {
    return event.nativeEvent.composedPath().filter(node => node instanceof Element);
}

function controlId(element, attribute, path, root) {
    const baseId = element.getAttribute(attribute);
    const optionId = element.getAttribute('data-onboarding-option-id');
    const id = optionId ? `${baseId}:${optionId}` : baseId;
    const scope = path.find(node => node.hasAttribute('data-onboarding-control-id'));
    let scopedId = id;
    if (scope) {
        const scopeId = scope.getAttribute('data-onboarding-control-id');
        const namespace = scopeId.slice(0, scopeId.lastIndexOf(':') + 1);
        scopedId = `${scopeId}:${namespace && id.startsWith(namespace) ? id.slice(namespace.length) : id}`;
    }
    const instances = Array.from(root.querySelectorAll(`[${attribute}]`))
        .filter(node => node.getAttribute(attribute) === baseId &&
            node.getAttribute('data-onboarding-option-id') === optionId);
    return instances.length > 1 ? `${scopedId}:item-${instances.indexOf(element) + 1}` : scopedId;
}

function targetUrl(element) {
    const url = new URL(element.href);
    if (!['https:', 'http:'].includes(url.protocol)) {
        return '[REDACTED]';
    }
    // Signing links and return URLs can carry credentials or personal values in their query/fragment.
    return `${url.origin}${url.pathname}`;
}

export default function OnboardingInteractionTracking({ pageName, children }) {
    const pageViewId = useGtmPageViewId(pageName);
    const configuration = useContext(ClientConfigurationContext);
    const interviews = useContext(GeaoContext);
    const enabled = configuration?.enableTagManager &&
        Boolean(configuration?.tagManagerUrl?.trim());

    const selectedRadioIds = useRef(new Map());

    const trackInteraction = useCallback((eventType, staticId, details = {}) => {
        if (!enabled) {
            return;
        }
        const scopedId = `${pageName.trim()}:${staticId}`;
        let radioFields;
        if (eventType === GtmEventType.RadioChange) {
            const { selectedOptionId, ...rest } = details;
            details = rest;
            const selectedRadioId = `${scopedId}:${selectedOptionId}`;
            const previouslySelectedRadioId = selectedRadioIds.current.get(scopedId) || '';
            selectedRadioIds.current.set(scopedId, selectedRadioId);
            radioFields = { selectedRadioId, previouslySelectedRadioId };
        }
        const metadata = buildGtmEventMetadata({
            pageName,
            pageViewId,
            eventType,
            agentInterviewId: interviews?.agentInterview?.interviewId,
            agencyInterviewId: interviews?.agencyInterview?.interviewId,
            previousPageName: getPreviousPageName(pageName),
            [idFields[eventType]]: scopedId,
            ...radioFields,
            ...details
        });
        if (!trackGtmEvent('Click', metadata)) {
            console.warn('GTM interaction tracking not available - window._gtm is undefined');
        }
    }, [enabled, interviews?.agentInterview?.interviewId, interviews?.agencyInterview?.interviewId, pageName, pageViewId]);

    const handleClick = (event) => {
        const path = eventElements(event);
        const element = path.find(node =>
            node.hasAttribute('data-onboarding-button-id') ||
            node.hasAttribute('data-onboarding-link-id')
        );
        if (path.some(node =>
            node.hasAttribute('data-gtm-button-id') ||
            node.hasAttribute('data-gtm-link-id') ||
            node.matches(':disabled, [disabled]:not([disabled="false"]), [aria-disabled="true"]')
        )) {
            return;
        }
        if (!element) {
            const scope = path.find(node => node.hasAttribute('data-onboarding-control-id'));
            const button = path.find(node => node.matches('button, [role="button"], input[type="button"], input[type="submit"]'));
            if (scope && button) {
                const widget = path.find(node => widgetActions[node.localName]);
                if (!widget) {
                    console.warn('GTM widget button requires a static action ID:', scope.getAttribute('data-onboarding-control-id'));
                    return;
                }
                const scopeId = controlId(scope, 'data-onboarding-control-id', [], event.currentTarget);
                const id = `${scopeId}:${widgetActions[widget.localName]}`;
                trackInteraction(GtmEventType.ButtonClick, id);
            }
            return;
        }
        if (element.hasAttribute('data-onboarding-button-id')) {
            trackInteraction(GtmEventType.ButtonClick,
                controlId(element, 'data-onboarding-button-id', path, event.currentTarget));
        } else {
            trackInteraction(GtmEventType.LinkClick,
                controlId(element, 'data-onboarding-link-id', path, event.currentTarget), {
                targetURL: targetUrl(element)
            });
        }
    };

    const handleChange = (event) => {
        const element = event.target;
        if (!(element instanceof HTMLInputElement) || element.disabled ||
            element.hasAttribute('data-gtm-radio-element-id') || element.hasAttribute('data-gtm-checkbox-id')) {
            return;
        }
        if (element.type === 'checkbox' && element.hasAttribute('data-onboarding-checkbox-id')) {
            trackInteraction(GtmEventType.CheckboxChange,
                controlId(element, 'data-onboarding-checkbox-id', eventElements(event), event.currentTarget), {
                checked: element.checked
            });
        } else if (element.type === 'radio' && element.checked && element.hasAttribute('data-onboarding-radio-id')) {
            const groupId = element.getAttribute('data-onboarding-radio-id');
            const optionId = element.getAttribute('data-onboarding-option-id');
            if (!optionId) {
                console.warn('GTM radio option requires a static option ID:', groupId);
                return;
            }
            trackInteraction(GtmEventType.RadioChange, groupId, { selectedOptionId: optionId });
        }
    };

    return (
        <OnboardingInteractionContext.Provider value={trackInteraction}>
            <div style={{ display: 'contents' }} onClickCapture={handleClick} onChangeCapture={handleChange}>
                {children}
            </div>
        </OnboardingInteractionContext.Provider>
    );
}
